/* eslint-disable import/no-unused-modules */
import {Options, TableDataRequest} from '@fl/cmsch-fe-library';
import {Opt} from 'ts-opt';

import {AnimalList} from 'api/gen/AnimalList';
import {AnimalType} from 'api/gen/AnimalType';
import {AnimalsBrowserFarm} from 'api/gen/AnimalsBrowserFarm';
import {AnimalsBrowserStable} from 'api/gen/AnimalsBrowserStable';
import {AvailableAnimal} from 'api/gen/AvailableAnimal';
import {BreedingFemale} from 'api/gen/BreedingFemale';
import {CabBreedingFemale} from 'api/gen/CabBreedingFemale';
import {CabCalf} from 'api/gen/CabCalf';
import {Calf} from 'api/gen/Calf';
import {EarTag} from 'api/gen/EarTag';
import {PagedModelBreedingFemale} from 'api/gen/PagedModelBreedingFemale';
import {PagedModelCabBreedingFemale} from 'api/gen/PagedModelCabBreedingFemale';
import {PagedModelCabCalf} from 'api/gen/PagedModelCabCalf';
import {PagedModelCalf} from 'api/gen/PagedModelCalf';
import {PagedModelStableToChoose} from 'api/gen/PagedModelStableToChoose';
import {Sex} from 'api/gen/Sex';
import {StableToChoose} from 'api/gen/StableToChoose';

import {BrowserLoadingId} from '../types/browser-loading-id';
import {BrowserType} from '../types/browser-type';

/** Action creators for animal-browser tables, exports, filters, and saved animal-list flows. */
export const animalBrowserAction = {
    getStablesTableData: (tableDataRequest: Opt<TableDataRequest<StableToChoose>>) => ({
        type: 'animalBrowser/GET_STABLES_DATA',
        payload: {tableDataRequest},
    }) as const,
    getStablesTableDataSuccess: (tableData: Opt<PagedModelStableToChoose>) => ({
        type: 'animalBrowser/GET_STABLES_DATA_SUCCESS',
        payload: tableData,
    }) as const,
    getcowBrowserTableData: (tableDataRequest: Opt<TableDataRequest<BreedingFemale>>, stableId: number) => ({
        type: 'animalBrowser/GET_COW_BROWSER_DATA',
        payload: {tableDataRequest, stableId},
    }) as const,
    getcowBrowserTableDataSuccess: (tableData: Opt<PagedModelBreedingFemale>) => ({
        type: 'animalBrowser/GET_COW_BROWSER_DATA_SUCCESS',
        payload: tableData,
    }) as const,
    setStableName: (stableInfo: AnimalsBrowserStable) => ({
        type: 'animalBrowser/SET_STABLE_NAME',
        payload: stableInfo,
    }) as const,
    clearStableName: () => ({
        type: 'animalBrowser/CLEAR_STABLE_NAME',
    }) as const,
    exportToCsv: (id: number) => ({
        type: 'animalBrowser/EXPORT_TO_CSV',
        payload: id,
    }) as const,
    getCalfBrowserTableData: (tableDataRequest: Opt<TableDataRequest<Calf>>, farmId: number) => ({
        type: 'animalBrowser/GET_CALF_BROWSER_DATA',
        payload: {tableDataRequest, farmId},
    }) as const,
    getCalfBrowserTableDataSuccess: (tableData: Opt<PagedModelCalf>) => ({
        type: 'animalBrowser/GET_CALF_BROWSER_DATA_SUCCESS',
        payload: tableData,
    }) as const,
    setFarmName: (farmInfo: AnimalsBrowserFarm) => ({
        type: 'animalBrowser/SET_FARM_NAME',
        payload: farmInfo,
    }) as const,
    clearFarmName: () => ({
        type: 'animalBrowser/CLEAR_FARM_NAME',
    }) as const,
    getCalvesCsv: (id: number) => ({
        type: 'animalBrowser/GET_CALVES_CSV',
        payload: id,
    }) as const,
    getProductionAreasInCentralEvidence: () => ({
        type: 'animalBrowser/GET_PRODUCTION_AREAS',
    }) as const,
    getProductionAreasInCentralEvidenceSuccess: (productionAreasInCentralEvidenceOptions: Options<string>) => ({
        type: 'animalBrowser/GET_PRODUCTION_AREAS_SUCCESS',
        payload: {productionAreasInCentralEvidenceOptions},
    }) as const,
    startPrint: () => ({
        type: 'animalBrowser/START_PRINT',
    }) as const,
    printFinished: () => ({
        type: 'animalBrowser/PRINT_FINISHED',
    }) as const,
    startExport: () => ({
        type: 'animalBrowser/START_EXPORT',
    }) as const,
    exportFinished: () => ({
        type: 'animalBrowser/EXPORT_FINISHED',
    }) as const,
    getStablesCsv: () => ({
        type: 'animalBrowser/GET_STABLES_CSV',
    }) as const,
    getCabFemales: (tableDataRequest: Opt<TableDataRequest<CabBreedingFemale>>,
    ) => ({
        type: 'animalBrowser/GET_CAB_FEMALES',
        payload: {tableDataRequest},
    }) as const,
    getCabFemalesSuccess: (tableData: Opt<PagedModelCabBreedingFemale>) => ({
        type: 'animalBrowser/GET_CAB_FEMALES_SUCCESS',
        payload: tableData,
    }) as const,
    filterCabFemales: (firstLoad?: 'FIRST_LOAD') => ({
        type: 'animalBrowser/FILTER_CAB_FEMALES',
        payload: firstLoad,
    }) as const,
    setCabFemalesExistInSession: (isFull: boolean) => ({
        type: 'animalBrowser/SET_CAB_FEMALES_EXIST',
        payload: {isFull},
    }) as const,
    getCabCalves: (tableDataRequest: Opt<TableDataRequest<CabCalf>>) => ({
        type: 'animalBrowser/GET_CAB_CALVES',
        payload: {tableDataRequest},
    }) as const,
    getCabCalvesSucces: (tableData: Opt<PagedModelCabCalf>) => ({
        type: 'animalBrowser/GET_CAB_CALVES_SUCCESS',
        payload: tableData,
    }) as const,
    setCabCalvesExistInSession: (isFull: boolean) => ({
        type: 'animalBrowser/SET_CAB_CALVES_EXIST',
        payload: {isFull},
    }) as const,
    setLoading: (id: BrowserLoadingId, loading: boolean) => ({
        type: 'animalBrowser/SET_LOADING',
        payload: {id, loading},
    }) as const,
    exportCabAnimals: (animalType: AnimalType) => ({
        type: 'animalBrowser/EXPORT_CAB_ANIMALS',
        payload: {animalType},
    }) as const,
    filterCabCalves: (firstLoad?: 'FIRST_LOAD') => ({
        type: 'animalBrowser/FILTER_CAB_CALVES',
        payload: firstLoad,
    }) as const,
    getOrganizationPtOptions: (searchingText: string) => ({
        type: 'animalBrowser/GET_ORGANIZATIONS_PT_OPTIONS',
        payload: searchingText,
    }) as const,
    getOrganizationsPtSuccess: (organizationsPtOptions: Options<number>) => ({
        type: 'animalBrowser/GET_ORGANIZATIONS_PT_OPTIONS_SUCCESS',
        payload: organizationsPtOptions,
    }) as const,
    getOrganizationInsOptions: (searchingText: string) => ({
        type: 'animalBrowser/GET_ORGANIZATION_INS_OPTIONS',
        payload: searchingText,
    }) as const,
    getOrganizationInsOptionsSuccess: (organizationInsOptions: Options<number>) => ({
        type: 'animalBrowser/GET_ORGANIZATION_INS_OPTIONS_SUCCESS',
        payload: organizationInsOptions,
    }) as const,
    getBreederOptions: (searchingText: string) => ({
        type: 'animalBrowser/GET_BREEDER_OPTIONS',
        payload: searchingText,
    }) as const,
    getBreederOptionsSuccess: (breederOptions: Options<number>) => ({
        type: 'animalBrowser/GET_BREEDER_OPTIONS_SUCCESS',
        payload: breederOptions,
    }) as const,
    getDistrictCodesOptions: (searchingText: string) => ({
        type: 'animalBrowser/GET_DISTRICT_CODES_OPTIONS',
        payload: searchingText,
    }) as const,
    getDistrictCodesOptionsSuccess: (districtCodesOptions: Options<number>) => ({
        type: 'animalBrowser/GET_DISTRICT_CODES_OPTIONS_SUCCESS',
        payload: districtCodesOptions,
    }) as const,
    getFarmOptions: (searchingText: string) => ({
        type: 'animalBrowser/GET_FARM_OPTIONS',
        payload: searchingText,
    }) as const,
    getFarmOptionsSuccess: (farmOptions: Options<number>) => ({
        type: 'animalBrowser/GET_FARM_OPTIONS_SUCCESS',
        payload: farmOptions,
    }) as const,
    getStableOptions: (searchingText: string) => ({
        type: 'animalBrowser/GET_STABLE_OPTIONS',
        payload: searchingText,
    }) as const,
    getStableOptionsSuccess: (stableOptions: Options<number>) => ({
        type: 'animalBrowser/GET_STABLE_OPTIONS_SUCCESS',
        payload: stableOptions,
    }) as const,
    getOrganizationCenterInsOptions: (searchingText: string) => ({
        type: 'animalBrowser/GET_ORGANIZATION_CENTER_INS_OPTIONS',
        payload: searchingText,
    }) as const,
    getOrganizationCenterInsOptionsSuccess: (organizationInsOptions: Options<number>) => ({
        type: 'animalBrowser/GET_ORGANIZATION_CENTER_INS_OPTIONS_SUCCESS',
        payload: organizationInsOptions,
    }) as const,
    getOrganizationCenterPtOptions: (searchingText: string) => ({
        type: 'animalBrowser/GET_ORGANIZATION_CENTER_PT_OPTIONS',
        payload: searchingText,
    }) as const,
    getOrganizationCenterPtOptionsSuccess: (organizationInsOptions: Options<number>) => ({
        type: 'animalBrowser/GET_ORGANIZATION_CENTER_PT_OPTIONS_SUCCESS',
        payload: organizationInsOptions,
    }) as const,
    clearData: () => ({
        type: 'animalBrowser/CLEAR_DATA',
    }) as const,
    firstCalveLoad: (firstLoad?: 'FIRST_LOAD') => ({
        type: 'animalBrowser/FIRST_CALVE_LOAD',
        payload: firstLoad === 'FIRST_LOAD',
    }) as const,
    firstBreedingFemaleLoad: (firstLoad?: 'FIRST_LOAD') => ({
        type: 'animalBrowser/FIRST_BREEDING_FEMALE_LOAD',
        payload: firstLoad === 'FIRST_LOAD',
    }) as const,
    toggleOverviewAnimalsListModal: () => ({
        type: 'animalBrowser/TOGGLE_OVERVIEW_ANIMALS_LIST_MODAL',
    }) as const,
    getAnimalLists: (animalType: AnimalType, withoutLoading?: boolean) => ({
        type: 'animalBrowser/GET_ANIMAL_LISTS',
        payload: {animalType, withoutLoading},
    }) as const,
    getAnimalListsSuccess: (animalLists: Array<AnimalList>, animalType: AnimalType) => ({
        type: 'animalBrowser/GET_ANIMAL_LISTS_SUCCESS',
        payload: {animalLists, animalType},
    }) as const,
    renameAnimalList: (animalListId: number, name: string, animalType: AnimalType) => ({
        type: 'animalBrowser/RENAME_ANIMAL_LIST',
        payload: {animalListId, name, animalType},
    }) as const,
    deleteAnimalList: (animalListId: number, animalType: AnimalType) => ({
        type: 'animalBrowser/DELETE_ANIMAL_LIST',
        payload: {animalListId, animalType},
    }) as const,
    createAnimalList: (name: string, animalType: AnimalType) => ({
        type: 'animalBrowser/CREATE_ANIMAL_LIST',
        payload: {name, animalType},
    }) as const,
    confirmOverrideAnimalList: (animalType: AnimalType, animalListId: number) => ({
        type: 'animalBrowser/CONFIRM_OVERRIDE_ANIMAL_LIST',
        payload: {animalType, animalListId},
    }) as const,
    createAnimalListFromIds: (name: string, animalType: AnimalType) => ({
        type: 'animalBrowser/CREATE_ANIMAL_LIST_FROM_IDS',
        payload: {name, animalType},
    }) as const,
    toggleCreateAnimalsListFromIdsModal: () => ({
        type: 'animalBrowser/TOGGLE_CREATE_ANIMALS_LIST_FROM_IDS_MODAL',
    }) as const,
    toggleOverrideAnimalListFromIdsVisibility: (animalListId: Opt<number>) => ({
        type: 'animalBrowser/TOGGLE_OVERRIDE_ANIMALS_LIST_FROM_IDS_MODAL',
        payload: {animalListId},
    }) as const,
    confirmOverrideAnimalListFromIds: (animalType: AnimalType, animalListId: number) => ({
        type: 'animalBrowser/CONFIRM_OVERRIDE_ANIMAL_LIST_FROM_IDS',
        payload: {animalType, animalListId},
    }) as const,
    setSelectedAnimalIds: (animalIds: Array<number>) => ({
        type: 'animalBrowser/SET_SELECTED_ANIMAL_IDS',
        payload: {animalIds},
    }) as const,
    toggleAddAnimalsModal: () => ({
        type: 'animalBrowser/TOGGLE_ADD_ANIMALS_MODAL',
    }) as const,
    getAvailableAnimals: (animalType: AnimalType, text: string) => ({
        type: 'animalBrowser/GET_AVAILABLE_ANIMALS',
        payload: {animalType, text},
    }) as const,
    getAvailableAnimalsSuccess: (availableAnimals: Array<AvailableAnimal>) => ({
        type: 'animalBrowser/GET_AVAILABLE_ANIMALS_SUCCESS',
        payload: {availableAnimals},
    }) as const,
    addAnimals: (animalType: AnimalType, animalIds: Array<number>) => ({
        type: 'animalBrowser/ADD_ANIMALS',
        payload: {animalType, animalIds},
    }) as const,
    addAnimalsFromCsv: (animalType: AnimalType, file: File) => ({
        type: 'animalBrowser/ADD_ANIMALS_FROM_CSV',
        payload: {animalType, file},
    }) as const,
    toggleAddAnimalsFromDBVisibility: () => ({
        type: 'animalBrowser/TOGGLE_ADD_ANIMALS_FROM_DB_VISIBILITY',
    }) as const,
    addAnimalsFromDB: (animalType: AnimalType, animalListId: number) => ({
        type: 'animalBrowser/ADD_ANIMALS_FROM_DB',
        payload: {animalType, animalListId},
    }) as const,
    getAnimalsCount: (animalType: AnimalType) => ({
        type: 'animalBrowser/GET_ANIMALS_COUNT',
        payload: {animalType},
    }) as const,
    deleteAnimals: (animalType: AnimalType) => ({
        type: 'animalBrowser/DELETE_ANIMALS',
        payload: {animalType},
    }) as const,
    getMotherHuid: (earTag: string, huid: string, animalType: AnimalType, comprehensive: BrowserType) => ({
        type: 'animalBrowser/GET_MOTHER_HUID',
        payload: {earTag, huid, animalType, comprehensive},
    }) as const,
    setBullsForExport: (earTags: Array<EarTag>) => ({
        type: 'animalBrowser/NUMBER_OF_CALVES',
        payload: earTags,
    }) as const,
    setFemalesForExport: (earTags: Array<EarTag>) => ({
        type: 'animalBrowser/NUMBER_OF_BREEDING_FEMALES',
        payload: earTags,
    }) as const,
    getBreedingFemalesForPrint: (id: number) => ({
        type: 'animalBrowser/GET_BREEDING_FEMALES_FOR_PRINT',
        payload: id,
    }) as const,
    getAnimalsForPrint: (id: number) => ({
        type: 'animalBrowser/GET_ANIMALS_FOR_PRINT',
        payload: id,
    }) as const,
    submitPrintModal: (sex: Sex) => ({
        type: 'animalBrowser/SUBMIT_PRINT_MODAL',
        payload: sex,
    }) as const,
};

/** Fetch action for the stable overview table. */
export type GetStablesDataAction = ReturnType<typeof animalBrowserAction.getStablesTableData>;
/** Request action for the breeding-female print flow. */
export type GetBreedingFemalesForPrintAction = ReturnType<typeof animalBrowserAction.getBreedingFemalesForPrint>;
/** Request action for the calf print flow. */
export type GetAnimalsForPrintAction = ReturnType<typeof animalBrowserAction.getAnimalsForPrint>;
/** Submit action for the PDF print modal. */
export type SubmitPrintModalAction = ReturnType<typeof animalBrowserAction.submitPrintModal>;
/** Loading-state action for exported bull tags. */
export type SetBullsForExportAction = ReturnType<typeof animalBrowserAction.setBullsForExport>;
/** Loading-state action for exported breeding-female tags. */
export type SetFemalesForExportAction = ReturnType<typeof animalBrowserAction.setFemalesForExport>;
/** Success action for loading the stable overview table. */
export type GetStablesDataSuccessAction = ReturnType<typeof animalBrowserAction.getStablesTableDataSuccess>;
/** Fetch action for the breeding-female table in stable detail. */
export type GetCowBrowserTableDataAction = ReturnType<typeof animalBrowserAction.getcowBrowserTableData>;
/** Success action for loading the breeding-female table in stable detail. */
export type GetCowBrowserTableDataSuccessAction = ReturnType<typeof animalBrowserAction.getcowBrowserTableDataSuccess>;
/** Sets the selected stable name in state. */
export type SetStableNameAction = ReturnType<typeof animalBrowserAction.setStableName>;
/** Clears the selected stable name from state. */
export type ClearStableNameAction = ReturnType<typeof animalBrowserAction.clearStableName>;
/** CSV export action for the stable overview. */
export type ExportToCsvAction = ReturnType<typeof animalBrowserAction.exportToCsv>;
/** Fetch action for the calf table in farm detail. */
export type GetCalfBrowserTableDataAction = ReturnType<typeof animalBrowserAction.getCalfBrowserTableData>;
/** Success action for loading the calf table in farm detail. */
export type GetCalfBrowserTableDataSuccessAction =
    ReturnType<typeof animalBrowserAction.getCalfBrowserTableDataSuccess>;
/** Sets the selected farm name in state. */
export type SetFarmNameAction = ReturnType<typeof animalBrowserAction.setFarmName>;
/** Clears the selected farm name from state. */
export type ClearFarmNameAction = ReturnType<typeof animalBrowserAction.clearFarmName>;
/** CSV export action for the calf overview. */
export type GetCalvesCsvAction = ReturnType<typeof animalBrowserAction.getCalvesCsv>;
/** Fetch action for the shared production-area options. */
export type GetProductionAreasInCentralEvidenceAction =
    ReturnType<typeof animalBrowserAction.getProductionAreasInCentralEvidence>;
/** Success action for loading shared production-area options. */
export type GetProductionAreasInCentralEvidenceSuccessAction =
    ReturnType<typeof animalBrowserAction.getProductionAreasInCentralEvidenceSuccess>;
/** Marks print mode as started. */
export type StartPrintAction = ReturnType<typeof animalBrowserAction.startPrint>;
/** Marks print mode as finished. */
export type PrintFinishedAction = ReturnType<typeof animalBrowserAction.printFinished>;
/** Marks CSV export as started. */
export type StartExportAction = ReturnType<typeof animalBrowserAction.startExport>;
/** Marks CSV export as finished. */
export type ExportFinishedAction = ReturnType<typeof animalBrowserAction.exportFinished>;
/** CSV export action for the stable overview. */
export type GetStablesCsvAction = ReturnType<typeof animalBrowserAction.getStablesCsv>;
/** Fetch action for comprehensive breeding-female rows. */
export type GetCabFemalesAction = ReturnType<typeof animalBrowserAction.getCabFemales>;
/** Success action for comprehensive breeding-female rows. */
export type GetCabFemalesSuccessAction = ReturnType<typeof animalBrowserAction.getCabFemalesSuccess>;
/** Filter trigger for the comprehensive breeding-female browser. */
export type FilterCabFemalesAction = ReturnType<typeof animalBrowserAction.filterCabFemales>;
/** Filter trigger for the comprehensive calf browser. */
export type FilterCabCalvesAction = ReturnType<typeof animalBrowserAction.filterCabCalves>;
/** Session-coverage flag for comprehensive breeding-female data. */
export type SetCabFemalesExistAction = ReturnType<typeof animalBrowserAction.setCabFemalesExistInSession>;
/** Fetch action for comprehensive calf rows. */
export type GetCabCalvesAction = ReturnType<typeof animalBrowserAction.getCabCalves>;
/** Success action for comprehensive calf rows. */
export type GetCabCalvesSuccessAction = ReturnType<typeof animalBrowserAction.getCabCalvesSucces>;
/** Session-coverage flag for comprehensive calf data. */
export type SetCabCalvesExistInSessionAction = ReturnType<typeof animalBrowserAction.setCabCalvesExistInSession>;
/** Loading-state action for animal-browser async flows. */
export type AnimalBrowserSetLoadingAction = ReturnType<typeof animalBrowserAction.setLoading>;
/** CSV export action for comprehensive animal-browser views. */
export type ExportCabAnimalsAction = ReturnType<typeof animalBrowserAction.exportCabAnimals>;
/** Fetch action for point-of-service organization options. */
export type GetOrganizationPtOptionsAction = ReturnType<typeof animalBrowserAction.getOrganizationPtOptions>;
/** Success action for point-of-service organization options. */
export type GetOrganizationPtOptionsSuccessAction = ReturnType<typeof animalBrowserAction.getOrganizationsPtSuccess>;
/** Fetch action for insemination organization options. */
export type GetOrganizationInsOptionsAction = ReturnType<typeof animalBrowserAction.getOrganizationInsOptions>;
/** Success action for insemination organization options. */
export type GetOrganizationInsOptionsSuccessAction =
    ReturnType<typeof animalBrowserAction.getOrganizationInsOptionsSuccess>;
/** Fetch action for breeder options. */
export type GetBreederOptionsAction = ReturnType<typeof animalBrowserAction.getBreederOptions>;
/** Success action for breeder options. */
export type GetBreederOptionsSuccessAction = ReturnType<typeof animalBrowserAction.getBreederOptionsSuccess>;
/** Fetch action for district-code options. */
export type GetDistrictCodesOptionsAction = ReturnType<typeof animalBrowserAction.getDistrictCodesOptions>;
/** Success action for district-code options. */
export type GetDistrictCodesOptionsSuccessAction =
    ReturnType<typeof animalBrowserAction.getDistrictCodesOptionsSuccess>;
/** Fetch action for farm options. */
export type GetFarmOptionsAction = ReturnType<typeof animalBrowserAction.getFarmOptions>;
/** Success action for farm options. */
export type GetFarmOptionsSuccessAction = ReturnType<typeof animalBrowserAction.getFarmOptionsSuccess>;
/** Fetch action for stable options. */
export type GetStableOptionsAction = ReturnType<typeof animalBrowserAction.getStableOptions>;
/** Success action for stable options. */
export type GetStableOptionsSuccessAction = ReturnType<typeof animalBrowserAction.getStableOptionsSuccess>;
/** Fetch action for organization-center insemination options. */
export type GetOrganizationCenterInsOptionsAction =
    ReturnType<typeof animalBrowserAction.getOrganizationCenterInsOptions>;
/** Success action for organization-center insemination options. */
export type GetOrganizationCenterInsOptionsSuccessAction =
    ReturnType<typeof animalBrowserAction.getOrganizationCenterInsOptionsSuccess>;
/** Fetch action for organization-center point-of-service options. */
export type GetOrganizationCenterPtOptionsAction =
    ReturnType<typeof animalBrowserAction.getOrganizationCenterPtOptions>;
/** Success action for organization-center point-of-service options. */
export type GetOrganizationCenterPtOptionsSuccessAction =
    ReturnType<typeof animalBrowserAction.getOrganizationCenterPtOptionsSuccess>;
/** Clears the comprehensive animal-browser state slice. */
export type ClearDataAction = ReturnType<typeof animalBrowserAction.clearData>;
/** Marks the comprehensive breeding-female screen as having completed its first load. */
export type FirstBreedingFemaleAction = ReturnType<typeof animalBrowserAction.firstBreedingFemaleLoad>;
/** Marks the comprehensive calf screen as having completed its first load. */
export type FirstCalveLoadAction = ReturnType<typeof animalBrowserAction.firstCalveLoad>;
/** Toggles the saved-list overview modal. */
export type ToggleOverviewAnimalsListModalAction =
    ReturnType<typeof animalBrowserAction.toggleOverviewAnimalsListModal>;
/** Fetch action for saved animal lists. */
export type GetAnimalListsAction = ReturnType<typeof animalBrowserAction.getAnimalLists>;
/** Success action for saved animal lists. */
export type GetAnimalListsSuccessAction = ReturnType<typeof animalBrowserAction.getAnimalListsSuccess>;
/** Rename action for a saved animal list. */
export type RenameAnimalListAction = ReturnType<typeof animalBrowserAction.renameAnimalList>;
/** Delete action for a saved animal list. */
export type DeleteAnimalListAction = ReturnType<typeof animalBrowserAction.deleteAnimalList>;
/** Create action for a saved animal list. */
export type CreateAnimalListAction = ReturnType<typeof animalBrowserAction.createAnimalList>;
/** Confirmation action for overwriting an existing saved animal list. */
export type ConfirmOverrideAnimalListAction = ReturnType<typeof animalBrowserAction.confirmOverrideAnimalList>;
/** Create action for a list from selected ids. */
export type CreateAnimalListFromIdsAction = ReturnType<typeof animalBrowserAction.createAnimalListFromIds>;
/** Toggles the create-from-ids modal. */
export type ToggleCreateAnimalsListFromIdsModalAction =
    ReturnType<typeof animalBrowserAction.toggleCreateAnimalsListFromIdsModal>;
/** Toggles the overwrite-from-ids modal. */
export type ToggleOverrideAnimalListFromIdsVisibilityAction =
    ReturnType<typeof animalBrowserAction.toggleOverrideAnimalListFromIdsVisibility>;
/** Confirmation action for overwriting a list from selected ids. */
export type ConfirmOverrideAnimalListFromIdsAction =
    ReturnType<typeof animalBrowserAction.confirmOverrideAnimalListFromIds>;
/** Stores the current animal selection. */
export type SetSelectedAnimalIdsAction = ReturnType<typeof animalBrowserAction.setSelectedAnimalIds>;
/** Toggles the add-animals modal. */
export type ToggleAddAnimalsModalAction = ReturnType<typeof animalBrowserAction.toggleAddAnimalsModal>;
/** Fetch action for available animals in the add-animals modal. */
export type GetAvailableAnimalsAction = ReturnType<typeof animalBrowserAction.getAvailableAnimals>;
/** Success action for available animals in the add-animals modal. */
export type GetAvailableAnimalsSuccessAction = ReturnType<typeof animalBrowserAction.getAvailableAnimalsSuccess>;
/** Adds selected animals to the current list. */
export type AddAnimalsAction = ReturnType<typeof animalBrowserAction.addAnimals>;
/** Adds animals from a CSV file. */
export type AddAnimalsFromCsvAction = ReturnType<typeof animalBrowserAction.addAnimalsFromCsv>;
/** Toggles the add-from-db modal. */
export type ToggleAddAnimalsFromDBVisibilityAction =
    ReturnType<typeof animalBrowserAction.toggleAddAnimalsFromDBVisibility>;
/** Adds animals from an existing saved list. */
export type AddAnimalsFromDBAction = ReturnType<typeof animalBrowserAction.addAnimalsFromDB>;
/** Fetch action for counting animals in the current selection. */
export type GetAnimalsCountAction = ReturnType<typeof animalBrowserAction.getAnimalsCount>;
/** Deletes the selected animals. */
export type DeleteAnimalsAction = ReturnType<typeof animalBrowserAction.deleteAnimals>;
/** Fetch action for mother-huid resolution. */
export type GetMotherHuidAction = ReturnType<typeof animalBrowserAction.getMotherHuid>;

/** Union of all animal-browser Redux actions. */
export type AnimalBrowserAction =
    | GetStablesDataAction
    | GetCowBrowserTableDataAction
    | SetBullsForExportAction
    | SetFemalesForExportAction
    | GetCowBrowserTableDataSuccessAction
    | SetStableNameAction
    | ClearStableNameAction
    | ExportToCsvAction
    | GetStablesDataSuccessAction
    | GetBreedingFemalesForPrintAction
    | GetAnimalsForPrintAction
    | SubmitPrintModalAction
    | GetCalfBrowserTableDataAction
    | GetCalfBrowserTableDataSuccessAction
    | SetFarmNameAction
    | ClearFarmNameAction
    | GetCalvesCsvAction
    | GetProductionAreasInCentralEvidenceAction
    | GetProductionAreasInCentralEvidenceSuccessAction
    | StartPrintAction
    | PrintFinishedAction
    | StartExportAction
    | ExportFinishedAction
    | GetStablesCsvAction
    | GetCabFemalesAction
    | GetCabFemalesSuccessAction
    | FilterCabFemalesAction
    | FilterCabCalvesAction
    | SetCabFemalesExistAction
    | GetCabCalvesAction
    | GetCabCalvesSuccessAction
    | SetCabCalvesExistInSessionAction
    | ExportCabAnimalsAction
    | AnimalBrowserSetLoadingAction
    | GetOrganizationPtOptionsAction
    | GetOrganizationInsOptionsAction
    | GetBreederOptionsAction
    | GetDistrictCodesOptionsAction
    | GetFarmOptionsAction
    | GetStableOptionsAction
    | GetOrganizationPtOptionsSuccessAction
    | GetOrganizationInsOptionsSuccessAction
    | GetBreederOptionsSuccessAction
    | GetDistrictCodesOptionsSuccessAction
    | GetFarmOptionsSuccessAction
    | GetStableOptionsSuccessAction
    | GetOrganizationCenterInsOptionsAction
    | GetOrganizationCenterPtOptionsAction
    | GetOrganizationCenterInsOptionsSuccessAction
    | GetOrganizationCenterPtOptionsSuccessAction
    | ClearDataAction
    | FirstBreedingFemaleAction
    | FirstCalveLoadAction
    | ToggleOverviewAnimalsListModalAction
    | GetAnimalListsAction
    | GetAnimalListsSuccessAction
    | RenameAnimalListAction
    | DeleteAnimalListAction
    | CreateAnimalListAction
    | ConfirmOverrideAnimalListAction
    | CreateAnimalListFromIdsAction
    | ToggleCreateAnimalsListFromIdsModalAction
    | ToggleOverrideAnimalListFromIdsVisibilityAction
    | ConfirmOverrideAnimalListFromIdsAction
    | SetSelectedAnimalIdsAction
    | ToggleAddAnimalsModalAction
    | GetAvailableAnimalsAction
    | GetAvailableAnimalsSuccessAction
    | AddAnimalsAction
    | AddAnimalsFromCsvAction
    | ToggleAddAnimalsFromDBVisibilityAction
    | AddAnimalsFromDBAction
    | GetAnimalsCountAction
    | DeleteAnimalsAction
    | GetMotherHuidAction
    ;
